/**
 * Canonical TopUpBalance — also consumed via `@vaiu/topup-balance` from developer/merchant apps.
 * Keep payment/* colocated under ./payment
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@vaiu/ui/button';
import { Input } from '@vaiu/ui/input';
import { Label } from '@vaiu/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@vaiu/ui/card';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@vaiu/ui/select';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@vaiu/ui/tabs';
import { Wallet, CreditCard, Building2, AlertCircle, DollarSign, X, ArrowLeft, Bitcoin, Smartphone, Globe, CheckCircle2, Plus } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import { platformApi, PaymentMethod } from '@/services/platformApi';
import { currencyService } from '@/services/currencyService';
import { CurrencyDisplay } from '@vaiu/ui';
import { emitTopUpBalanceUpdate } from '@/utils/lightEventEmitter';
import { resolveStripePaymentMethodId } from '@/utils/stripePaymentMethod';
import { meetsStripeMinimum, getStripeMinimumMessage, getStripeMinimum, stripeSupportsCurrency, currencyListIncludes } from '@/utils/stripeMinimums';
import { PaymentMethodATM } from './payment/PaymentMethodATM';
import { PaymentMethodPayPal } from './payment/PaymentMethodPayPal';
import { PaymentMethodDigitalWallet } from './payment/PaymentMethodDigitalWallet';
import { PaymentMethodCardForm } from './payment/PaymentMethodCardForm';
import { PaymentMethodBitcoin } from './payment/PaymentMethodBitcoin';
import { PaymentMethodEthereum } from './payment/PaymentMethodEthereum';
import { isCardToTokenEnabled } from '@/config/cardToTokenConfig';
import { i18n } from '@/utils/i18n';
import {
  closePayPalFundingTab,
  createPayPalFundingSessionId,
  goToPayPal,
  isFramedWindow,
  navigatePayPalFundingTab,
  openPayPalFundingTab,
  paypalApprovalUrl,
  resolvePayPalCallbackBaseUrl,
  toAbsolutePayPalUrl,
} from '@vaiu/utils';
import VisaLogo from '@/assets/brand-logos/visa-logo.svg';
import MastercardLogo from '@/assets/brand-logos/mastercard-logo.svg';
import AmexLogo from '@/assets/brand-logos/amex-logo.svg';
import DiscoverLogo from '@/assets/brand-logos/discover-logo.svg';
import JcbLogo from '@/assets/brand-logos/jcb-logo.svg';
import RbkoLogo from '@/assets/brand-logos/RBKo-logo.svg';

type FundingSource = 'balance' | 'card' | 'bank' | 'paypal' | 'wallet' | 'bitcoin' | 'ethereum';

interface TopUpBalanceProps {
  currentBalance: number;
  requiredAmount: number;
  currency: string;
  onSuccess: (newBalance: number, fundingDetails?: {
    source: string;
    amount: number;
    currency: string;
    paymentMethodType?: string;
    last4?: string;
    brand?: string;
  }) => void;
  onCancel: () => void;
  isOpen: boolean;
  context?: {
    type: 'token-creation' | 'profile' | string;
    returnUrl?: string;  // Full return URL (origin + port + path + query params)
    tokenAmount?: number;
    tokenCurrency?: string;
    tokenMessage?: string;
    tokenKey?: string;
  };
  initialTab?: 'atm' | 'card' | 'digital-wallet' | 'paypal';
  lockedTab?: 'atm' | 'card' | 'digital-wallet' | 'paypal';
  selectedFundingSource?: FundingSource;
  onFundingSourceChange?: (source: FundingSource) => void;
  balances?: Record<string, number>;
  amountDecimals?: number;
  balanceSubmitFormId?: string;
}

const getCardBrandPresentation = (brand?: string) => {
  switch (brand?.toLowerCase()) {
    case 'visa':
      return { label: 'Visa', logo: VisaLogo, className: 'bg-[#1434cb] text-white', mutedClassName: 'text-blue-100' };
    case 'mastercard':
      return { label: 'Mastercard', logo: MastercardLogo, className: 'bg-[#151515] text-white', mutedClassName: 'text-zinc-300' };
    case 'amex':
    case 'american_express':
      return { label: 'American Express', logo: AmexLogo, className: 'bg-[#006fc9] text-white', mutedClassName: 'text-sky-100' };
    case 'discover':
      return { label: 'Discover', logo: DiscoverLogo, className: 'bg-[#1c1c1c] text-white', mutedClassName: 'text-zinc-300' };
    case 'jcb':
      return { label: 'JCB', logo: JcbLogo, className: 'bg-[#0b4ea2] text-white', mutedClassName: 'text-blue-100' };
    default:
      return { label: brand || 'Card', logo: null, className: 'bg-slate-800 text-white', mutedClassName: 'text-slate-300' };
  }
};

const getBankBrandPresentation = (accountName?: string) => {
  const normalizedName = accountName?.toLowerCase() || '';
  if (normalizedName.includes('bnp')) {
    return { label: accountName || 'BNP Paribas', logo: '/img/banks/bnp-logo.png', className: 'bg-[#00965e] text-white', mutedClassName: 'text-emerald-100' };
  }
  if (normalizedName.includes('ing')) {
    return { label: accountName || 'ING', logo: '/img/banks/ing-logo.png', className: 'bg-[#ff6200] text-white', mutedClassName: 'text-orange-100' };
  }
  if (normalizedName.includes('rbko') || normalizedName.includes('royal bank of kosovo')) {
    return { label: accountName || 'RBKO', logo: RbkoLogo, className: 'bg-[#005ca9] text-white', mutedClassName: 'text-blue-100' };
  }
  return { label: accountName || 'Bank account', logo: null, className: 'bg-emerald-800 text-white', mutedClassName: 'text-emerald-100' };
};

const FUNDING_TILE =
  'box-border h-[100px] w-[156px] min-h-[100px] min-w-[156px] max-h-[100px] max-w-[156px] shrink-0 snap-center overflow-hidden rounded-lg p-2.5 text-left';

const fundingOutlineTileClass = (selected: boolean) =>
  `${FUNDING_TILE} border-2 transition-colors ${
    selected
      ? 'border-blue-500 bg-blue-50 text-blue-950'
      : 'border-border bg-background hover:border-blue-300 hover:bg-blue-50/60'
  }`;

const fundingBrandTileClass = (brandClassName: string, selected: boolean) =>
  `${FUNDING_TILE} relative border-2 shadow-sm transition-transform ${brandClassName} ${
    selected ? 'border-white ring-2 ring-blue-500' : 'border-transparent hover:-translate-y-0.5'
  }`;

const TopUpBalance: React.FC<TopUpBalanceProps> = ({
  currentBalance,
  requiredAmount,
  currency,
  onSuccess,
  onCancel,
  isOpen,
  context,
  initialTab,
  lockedTab,
  selectedFundingSource,
  onFundingSourceChange,
  balances,
  amountDecimals = 2,
  balanceSubmitFormId
}) => {
  const { toast } = useToast();
  const { isGuest } = useAuth();
  const paypalFundingEnabled = !isFramedWindow();
  const navigate = useNavigate();
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedMethodId, setSelectedMethodId] = useState<string>('');
  const [topUpAmount, setTopUpAmount] = useState<number>(0);
  const [topUpAmountInput, setTopUpAmountInput] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paypalApprovalHref, setPaypalApprovalHref] = useState<string>('');
  const [acceptableCurrencies, setAcceptableCurrencies] = useState<{
    stripe: string[];
    bank: string[];
    paypal: string[];
    bitcoin?: string[];
    ethereum?: string[];
  }>({ 
    // Card rails (incl. CHF) available immediately — do not wait on /currency/acceptable.
    stripe: ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CHF'],
    bank: [], 
    paypal: [], 
    bitcoin: [], 
    ethereum: [] 
  });
  const [currencyDecimals, setCurrencyDecimals] = useState<number>(2);
  const [showAddCardForm, setShowAddCardForm] = useState(false);
  const [showDigitalWallet, setShowDigitalWallet] = useState(false);
  /** Set after card/wallet charge succeeds — hides PayPal + duplicate charge controls */
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [selectedTab, setSelectedTab] = useState<string>(
    initialTab === 'paypal' && !paypalFundingEnabled ? 'card' : (initialTab ?? 'card')
  );
  /** True after user changes payment tab; avoids auto-switching away from PayPal they chose */
  const userPickedPaymentTabRef = useRef(false);
  const [debugInfo, setDebugInfo] = useState<string[]>([]);
  const stripeOk =
    stripeSupportsCurrency(currency) || currencyListIncludes(acceptableCurrencies.stripe, currency);
  const bankOk = currencyListIncludes(acceptableCurrencies.bank, currency);
  const bitcoinOk =
    currencyListIncludes(acceptableCurrencies.bitcoin, currency) || currency.toUpperCase() === 'BTC';
  const ethereumOk =
    currencyListIncludes(acceptableCurrencies.ethereum, currency) || currency.toUpperCase() === 'ETH';
  const paypalOffered =
    paypalFundingEnabled && currencyListIncludes(acceptableCurrencies.paypal, currency);

  // Helper function to round amount to currency decimal places
  const roundToCurrency = (amount: number): number => {
    return parseFloat(amount.toFixed(currencyDecimals));
  };

  const isTokenCreationFlow = context?.type === 'token-creation';
  const activeFundingSource: FundingSource = !paypalFundingEnabled
    ? (selectedFundingSource === 'paypal' || selectedTab === 'paypal'
        ? 'card'
        : (selectedFundingSource ??
          (selectedTab === 'digital-wallet' ? 'wallet' : 'card')))
    : selectedFundingSource ??
      (selectedTab === 'paypal'
        ? 'paypal'
        : selectedTab === 'digital-wallet'
          ? 'wallet'
          : 'card');
  const lockedPaymentTab =
    isTokenCreationFlow && lockedTab === 'paypal' && !paypalFundingEnabled
      ? undefined
      : isTokenCreationFlow
        ? lockedTab
        : undefined;
  const tokenCreationChargeAmount = isTokenCreationFlow
    ? roundToCurrency(requiredAmount)
    : roundToCurrency(topUpAmount);
  const chargeCardLabel = isTokenCreationFlow
    ? i18n.__('vaiu.topup.funding.charge_card_and_create')
    : i18n.__('vaiu.topup.funding.charge_card');
  const chargingCardLabel = isTokenCreationFlow
    ? i18n.__('vaiu.topup.funding.charging_card_and_creating')
    : i18n.__('vaiu.topup.funding.charging_card');
  const cardTabLabel = isTokenCreationFlow ? i18n.__('vaiu.topup.funding.cards_and_banks') : i18n.__('vaiu.topup.tabs.card');
  const amountLabel = isTokenCreationFlow
    ? i18n.__('vaiu.topup.funding.amount')
    : i18n.__('vaiu.topup.amount_label').replace('{currency}', currency);
  const topUpTitle = isGuest
    ? i18n.__('vaiu.topup.guest_title')
    : i18n.__('vaiu.topup.title');
  const tokenCurrencyBalance = balances?.[currency] ?? currentBalance;
  const balanceCanFundToken = roundToCurrency(tokenCurrencyBalance) >= tokenCreationChargeAmount;
  const hasTokenCreationAmount = tokenCreationChargeAmount > 0;

  const selectFundingSource = (source: FundingSource) => {
    onFundingSourceChange?.(source);
    if (source === 'card' || source === 'bank') {
      setSelectedTab('card');
      setSelectedMethodId(source === 'bank' ? '' : pickDefaultSavedCardMethodId());
      setShowAddCardForm(source === 'card' && !pickDefaultSavedCardMethodId());
    } else if (source === 'paypal') {
      setSelectedTab('paypal');
      setSelectedMethodId('paypal-funding');
    } else if (source === 'wallet' || source === 'bitcoin' || source === 'ethereum') {
      setSelectedTab('digital-wallet');
      setSelectedMethodId('');
    }
  };

  useEffect(() => {
    if (lockedPaymentTab) {
      setSelectedTab(lockedPaymentTab);
    } else if (initialTab) {
      setSelectedTab(initialTab);
    }
  }, [initialTab, lockedPaymentTab]);

  useEffect(() => {
    if (isTokenCreationFlow && requiredAmount > 0) {
      const chargeAmount = roundToCurrency(requiredAmount);
      setTopUpAmount(chargeAmount);
      setTopUpAmountInput(chargeAmount.toString());
    }
  }, [isTokenCreationFlow, requiredAmount, currencyDecimals]);

  // Declare amounts state BEFORE getAvailableTabs to avoid initialization order issues
  const [amounts, setAmounts] = useState<{
    shortfall: number;
    minimumTopUp: number;
    roundedRequired: number;
    roundedCurrent: number;
  }>({
    shortfall: 0,
    minimumTopUp: 1.00,
    roundedRequired: requiredAmount,
    roundedCurrent: currentBalance
  });

  const isSavedCardOrBankMethodId = (methodId: string) => {
    const method = paymentMethods.find((m) => m.id === methodId);
    return method?.type === 'stripe' || method?.type === 'plaid';
  };

  const pickDefaultSavedCardMethodId = (methods: PaymentMethod[] = paymentMethods) => {
    const saved = methods.filter((m) => m.type === 'stripe');
    const chosen = saved.find((m) => m.isDefault) ?? saved[0];
    return chosen?.id ?? '';
  };

  // Helper function to get available tabs based on currency support
  const getAvailableTabs = useMemo(() => {
    const tabs: string[] = [];
    
    // Safely access amounts.shortfall with a fallback to 0
    const shortfall = amounts.shortfall ?? 0;
    
    // ATM tab - only if card-to-token enabled, shortfall exists, and Stripe supports currency
    if (shortfall > 0 && isCardToTokenEnabled() && stripeOk) {
      tabs.push('atm');
    }
    
    // Card tab - only if Stripe supports currency
    if (stripeOk) {
      tabs.push('card');
    }

    // Digital Wallet (card rails) before PayPal
    if (stripeOk) {
      tabs.push('digital-wallet');
    }

    // PayPal tab — full-page only; embed checkout cannot complete PayPal
    if (paypalOffered) {
      tabs.push('paypal');
    }
    
    return tabs;
  }, [paypalOffered, amounts.shortfall, acceptableCurrencies, currency]);

  // Prefer card / wallet / ATM over PayPal when picking a fallback tab (not raw getAvailableTabs[0])
  const firstPreferredPaymentTab = useCallback((tabs: string[]) => {
    const order = ['atm', 'card', 'digital-wallet', 'paypal'];
    return order.find(t => tabs.includes(t)) ?? tabs[0];
  }, []);

  // Keep tab valid; if current selection disappears, fall back to preferred order
  useEffect(() => {
    if (getAvailableTabs.length > 0) {
      if (lockedPaymentTab && getAvailableTabs.includes(lockedPaymentTab)) {
        if (selectedTab !== lockedPaymentTab) {
          setSelectedTab(lockedPaymentTab);
        }
        return;
      }
      if (!getAvailableTabs.includes(selectedTab)) {
        setSelectedTab(firstPreferredPaymentTab(getAvailableTabs));
      }
    }
  }, [getAvailableTabs, selectedTab, firstPreferredPaymentTab, lockedPaymentTab]);

  // While sheet is open, prefer card/ATM/wallet over PayPal whenever tabs update — unless the user
  // explicitly changed tab (e.g. chose PayPal). Fixes: PayPal-only flash then Stripe loads later.
  useEffect(() => {
    if (!isOpen || userPickedPaymentTabRef.current) return;
    if (lockedPaymentTab) return;
    if (getAvailableTabs.length === 0) return;
    const preferred = firstPreferredPaymentTab(getAvailableTabs);
    if (selectedTab !== preferred) {
      setSelectedTab(preferred);
    }
  }, [isOpen, getAvailableTabs, firstPreferredPaymentTab, selectedTab, lockedPaymentTab]);

  useEffect(() => {
    if (!isOpen) {
      userPickedPaymentTabRef.current = false;
    }
  }, [isOpen]);

  // Calculate shortfall and minimum top-up with proper currency rounding
  const calculateAmounts = async () => {
    try {
      const currencyDetails = await currencyService.getCurrencyDetails(currency);
      setCurrencyDecimals(currencyDetails.decimals);
      
      // Round amounts to proper decimal places for this currency
      const roundedRequired = parseFloat(requiredAmount.toFixed(currencyDetails.decimals));
      const roundedCurrent = parseFloat(currentBalance.toFixed(currencyDetails.decimals));
      // Calculate shortfall and round to avoid floating-point precision errors
      const shortfallRaw = Math.max(0, roundedRequired - roundedCurrent);
      const shortfall = parseFloat(shortfallRaw.toFixed(currencyDetails.decimals));
      // For Stripe/card payments, enforce Stripe minimum (e.g., $0.50 for USD). For PayPal, use shortfall directly.
      const stripeMinimum = getStripeMinimum(currency);
      const minimumTopUp = Math.max(shortfall, parseFloat(stripeMinimum.toFixed(currencyDetails.decimals)));
      
      return { shortfall, minimumTopUp, roundedRequired, roundedCurrent, decimals: currencyDetails.decimals };
    } catch (error) {
      console.warn('Failed to get currency details, using defaults:', error);
      setCurrencyDecimals(2);
      const shortfallRaw = Math.max(0, requiredAmount - currentBalance);
      const shortfall = parseFloat(shortfallRaw.toFixed(2)); // Round to 2 decimal places for default
      const stripeMinimum = getStripeMinimum(currency);
      const minimumTopUp = Math.max(shortfall, stripeMinimum);
      return { shortfall, minimumTopUp, roundedRequired: requiredAmount, roundedCurrent: currentBalance, decimals: 2 };
    }
  };

  useEffect(() => {
    if (isOpen) {
      setPaymentCompleted(false);
      fetchPaymentMethods();
      fetchAcceptableCurrencies();
      calculateAmounts().then(calculatedAmounts => {
        setAmounts(calculatedAmounts);
        // For cash-token creation, charge the full token reserve, not only the balance shortfall.
        const decimals = calculatedAmounts.decimals || currencyDecimals;
        const amountToSet = isTokenCreationFlow
          ? calculatedAmounts.roundedRequired
          : calculatedAmounts.minimumTopUp;
        setTopUpAmount(amountToSet);
        setTopUpAmountInput(amountToSet.toFixed(decimals));
      });
    }
  }, [isOpen, requiredAmount, currentBalance, currency, isGuest, isTokenCreationFlow]);

  // Auto-select PayPal when PayPal tab is selected (never after card charge completed)
  useEffect(() => {
    if (paymentCompleted) return;
    if (selectedTab === 'paypal' && paypalOffered) {
      setSelectedMethodId('paypal-funding');
    }
  }, [selectedTab, currency, acceptableCurrencies.paypal, paymentCompleted]);

  // Card tab must never keep PayPal selected — that made "Charge my card" redirect to PayPal
  useEffect(() => {
    if (paymentCompleted || selectedTab !== 'card' || showAddCardForm) return;
    if (selectedMethodId !== 'paypal-funding') return;

    const savedId = pickDefaultSavedCardMethodId();
    if (savedId) {
      setSelectedMethodId(savedId);
    } else {
      setSelectedMethodId('add-new-card');
      setShowAddCardForm(true);
    }
  }, [
    selectedTab,
    selectedMethodId,
    paymentMethods,
    paymentCompleted,
    showAddCardForm,
  ]);

  // Ref to prevent duplicate digital wallet payment processing
  const processingDigitalWalletRef = useRef(false);
  
  // Define handleDigitalWalletPayment BEFORE the useEffect that uses it
  const handleDigitalWalletPayment = useCallback(async (paymentMethod: any) => {
    // CRITICAL: Prevent duplicate processing
    if (processingDigitalWalletRef.current) {
      console.warn('[TopUpBalance] Digital wallet payment already processing, ignoring duplicate call');
      throw new Error('Payment already processing');
    }

    if (loading) {
      console.warn('[TopUpBalance] Another payment already in progress, ignoring digital wallet call');
      throw new Error('Another payment is already in progress');
    }
    
    processingDigitalWalletRef.current = true;
    setLoading(true);
    setError(null);
    
    // Mobile-friendly status update
    const walletType = paymentMethod.wallet?.type === 'applePay' ? 'Apple Pay' : 
                      paymentMethod.wallet?.type === 'googlePay' ? 'Google Pay' : 'Digital Wallet';
    
    // Mobile debug
    const roundedTopUpAmount = isTokenCreationFlow
      ? tokenCreationChargeAmount
      : parseFloat(topUpAmount.toFixed(currencyDecimals));

    setDebugInfo([`🎯 ${walletType} payment started`, `💰 Amount: ${currency} ${roundedTopUpAmount.toFixed(currencyDecimals)}`]);
    
    toast({
      title: i18n.__('vaiu.topup.toast.processing_wallet', { walletType }),
      description: i18n.__('vaiu.topup.toast.processing_payment', { currency, amount: roundedTopUpAmount.toFixed(currencyDecimals) }),
    });
    
      console.log('[TopUpBalance] handleDigitalWalletPayment called:', {
        paymentMethodId: paymentMethod.id,
        walletType: paymentMethod.wallet?.type,
        amount: roundedTopUpAmount,
        currency,
        isGuest,
        currentBalance
      });

    try {
      console.log('[TopUpBalance] Processing digital wallet payment with Stripe...', {
        roundedAmount: roundedTopUpAmount,
        originalAmount: isTokenCreationFlow ? tokenCreationChargeAmount : topUpAmount,
        decimals: currencyDecimals
      });
      
      // Process the digital wallet payment through Stripe
      let result: any;
      try {
        // Show processing status for mobile
        toast({
          title: i18n.__('vaiu.topup.toast.connecting'),
          description: i18n.__('vaiu.topup.toast.please_wait'),
        });
        
        result = await platformApi.topUpWithStripe({
          amount: roundedTopUpAmount,
          currency,
          paymentMethodId: paymentMethod.id
        });
        
        console.log('[TopUpBalance] Stripe payment result:', {
          success: result?.success,
          hasResult: !!result,
          resultKeys: result ? Object.keys(result) : [],
          fullResult: result
        });
      } catch (apiError: any) {
        console.error('[TopUpBalance] API call failed:', apiError);
        const errorMsg = apiError?.response?.data?.error || apiError?.message || apiError?.error || 'Failed to process payment with Stripe';
        
        // Show error on mobile
        toast({
          title: i18n.__('vaiu.topup.error.payment_failed'),
          description: i18n.__('vaiu.topup.error.api_error', { error: errorMsg }),
          variant: "destructive",
        });
        
        throw new Error(errorMsg);
      }

      if (!result || !result.success) {
        console.error('[TopUpBalance] Payment failed:', {
          result,
          error: (result as any)?.error,
          message: (result as any)?.message
        });
        
        const errorMsg = (result as any)?.error || (result as any)?.message || 'Digital wallet payment failed';
        
        // Show error on mobile
        toast({
          title: i18n.__('vaiu.topup.error.payment_not_successful'),
          description: errorMsg,
          variant: "destructive",
        });
        
        throw new Error(errorMsg);
      }

      console.log('[TopUpBalance] Payment successful, refreshing balances...');
      
      // CRITICAL: Clear sessionStorage immediately after successful payment to prevent recovery loops
      try {
        sessionStorage.removeItem('pendingDigitalWalletPayment');
      } catch (e) {
        console.warn('[TopUpBalance] Failed to clear sessionStorage:', e);
      }
      
      // Mobile debug
      setDebugInfo(prev => [...prev, `✅ Payment processed successfully`]);

      // Refresh balance from API to get accurate balance
      const balances = await platformApi.getBalances();
      const newBalance = balances[currency] || currentBalance;

      console.log('[TopUpBalance] Updated balance:', {
        currency,
        oldBalance: currentBalance,
        newBalance,
        allBalances: balances
      });

      const formattedAmount = await currencyService.formatCurrency(roundedTopUpAmount, currency);
      const walletType = paymentMethod.wallet?.type === 'applePay' ? 'Apple Pay' : 
                        paymentMethod.wallet?.type === 'googlePay' ? 'Google Pay' : 'Digital Wallet';

      toast({
        title: isGuest ? i18n.__('vaiu.topup.success.payment') : i18n.__('vaiu.topup.success.topup'),
        description: isGuest 
          ? i18n.__('vaiu.topup.success.paid_amount').replace('{amount}', formattedAmount).replace('{method}', walletType)
          : i18n.__('vaiu.topup.success.balance_added').replace('{amount}', formattedAmount).replace('{method}', walletType),
      });

      // Emit balance update event with actual balance from API
      emitTopUpBalanceUpdate(
        roundedTopUpAmount,
        currency,
        currentBalance,
        result.transactionId || `topup_${Date.now()}`,
        newBalance // Pass actual fetched balance
      );

      // Prepare funding details
      const fundingDetails = {
        source: 'digital_wallet',
        amount: roundedTopUpAmount,
        currency,
        paymentMethodType: 'digital_wallet',
        walletType: walletType
      };

      console.log('[TopUpBalance] Calling onSuccess callback...');
      
      // Mobile debug
      setDebugInfo(prev => [...prev, `📞 Calling success callback with balance: ${newBalance}`]);

      // Notify parent component with refreshed balance
      onSuccess(newBalance, fundingDetails);
      setShowDigitalWallet(false);
      
      console.log('[TopUpBalance] Digital wallet payment completed successfully');
      
      // Clear debug info after success
      setTimeout(() => setDebugInfo([]), 5000);
    } catch (error: any) {
      console.error('[TopUpBalance] Digital wallet payment failed:', error);
      setError(error.message || i18n.__('vaiu.topup.error.digital_wallet'));
      
      // Mobile debug
      setDebugInfo(prev => [...prev, `❌ Error: ${error.message}`]);
      
      toast({
        title: i18n.__('vaiu.topup.error.payment_failed'),
        description: error.message || i18n.__('vaiu.topup.error.digital_wallet'),
        variant: 'destructive',
      });
      throw error; // Re-throw so DigitalWalletButton can handle it
    } finally {
      setLoading(false);
      processingDigitalWalletRef.current = false; // CRITICAL: Reset guard
    }
  }, [
    topUpAmount,
    tokenCreationChargeAmount,
    isTokenCreationFlow,
    currency,
    currencyDecimals,
    currentBalance,
    isGuest,
    onSuccess,
    loading,
  ]);

  // Recover pending digital wallet payments (for mobile scenarios)
  useEffect(() => {
    const checkPendingPayment = async () => {
      try {
        const pendingPaymentStr = sessionStorage.getItem('pendingDigitalWalletPayment');
        if (!pendingPaymentStr) return;

        const pendingPayment = JSON.parse(pendingPaymentStr);
        const { paymentMethodId, amount, currency: pendingCurrency, timestamp } = pendingPayment;

        // Only recover payments from the last 5 minutes
        if (Date.now() - timestamp > 5 * 60 * 1000) {
          console.log('[TopUpBalance] Pending payment expired, clearing');
          sessionStorage.removeItem('pendingDigitalWalletPayment');
          return;
        }

        // Only recover if currency matches and component is open
        // CRITICAL: Also check if not already processing to prevent duplicates
        if (pendingCurrency === currency && isOpen && !processingDigitalWalletRef.current && !loading) {
          console.log('[TopUpBalance] Recovering pending digital wallet payment:', pendingPayment);
          
          // Create a mock payment method object with the stored ID
          const mockPaymentMethod = {
            id: paymentMethodId,
            type: 'card',
            wallet: { type: 'googlePay' }
          };

          // Process the payment
          try {
            await handleDigitalWalletPayment(mockPaymentMethod);
            // Clear sessionStorage after successful recovery
            sessionStorage.removeItem('pendingDigitalWalletPayment');
          } catch (error) {
            console.error('[TopUpBalance] Recovery failed, keeping in sessionStorage for retry:', error);
            // Don't clear sessionStorage on error - allow retry
          }
        } else {
          console.log('[TopUpBalance] Skipping recovery - already processing or conditions not met:', {
            currencyMatch: pendingCurrency === currency,
            isOpen,
            alreadyProcessing: processingDigitalWalletRef.current,
            loading
          });
        }
      } catch (error) {
        console.error('[TopUpBalance] Error recovering pending payment:', error);
        sessionStorage.removeItem('pendingDigitalWalletPayment');
      }
    };

    // Check on mount and when page becomes visible (for mobile)
    checkPendingPayment();
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkPendingPayment();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isOpen, currency, handleDigitalWalletPayment]);

  const fetchPaymentMethods = async (): Promise<PaymentMethod[]> => {
    try {
      // Fetch payment methods for ALL users (including guests)
      // Guest users have Stripe customer records and can have saved cards
      const methods = await platformApi.getPaymentMethods();
      
      // Filter for payment methods that can be used for top-up (Stripe cards and Bitcoin)
      // NOTE: Plaid is kept in codebase but not used - we use Stripe Financial Connections for bank accounts
      // Stripe bank accounts are fetched separately via getStripeBankAccounts()
      let topUpMethods = methods.filter(m => m.type === 'stripe' || m.type === 'bitcoin');
      // Explicitly exclude Plaid banks - we use Stripe Financial Connections instead
      // Plaid code remains in codebase but is not actively used
      
      // Fetch Stripe bank accounts (Financial Connections) - this is now the primary bank account method
      try {
        // getStripeBankAccounts() already returns PaymentMethod[] with source field set
        const stripeBanks = await platformApi.getStripeBankAccounts();
        // Debug: Log the API response to verify structure
        console.log('[TopUpBalance] Stripe banks from API:', stripeBanks);
        
        // Stripe banks are already in PaymentMethod format, just add them to the list
        topUpMethods = [...topUpMethods, ...stripeBanks];
      } catch (stripeBankError) {
        // If Stripe bank fetch fails, continue with other payment methods
        // This allows the flow to work even if Stripe Financial Connections is not enabled
        console.warn('Failed to fetch Stripe bank accounts (may not be enabled):', stripeBankError);
      }
      
      if (paypalFundingEnabled) {
        topUpMethods.push({
          id: 'paypal-funding',
          type: 'paypal' as const,
          details: {},
          isDefault: false,
          createdAt: new Date().toISOString()
        });
      }
      
      setPaymentMethods(topUpMethods);
      
      // Auto-select a saved card/bank — never auto-select PayPal (avoids accidental redirect)
      const savedStripeMethods = topUpMethods.filter(
        (m) => m.type === 'stripe' || m.type === 'plaid'
      );
      const defaultMethod =
        savedStripeMethods.find((m) => m.isDefault) ?? savedStripeMethods[0];
      if (defaultMethod?.id) {
        setSelectedMethodId(defaultMethod.id);
      }
      
      return topUpMethods;
    } catch (error) {
      console.error('Failed to fetch payment methods:', error);
      
      // If fetch fails for guest users, provide PayPal as fallback
      // This ensures guest users can still make payments even if the API call fails
      if (isGuest && paypalFundingEnabled) {
        const fallbackMethods: PaymentMethod[] = [{
          id: 'paypal-funding',
          type: 'paypal' as const,
          details: {},
          isDefault: false,
          createdAt: new Date().toISOString()
        }];
        setPaymentMethods(fallbackMethods);
        setError('Using fallback payment methods');
        return fallbackMethods;
      } else {
        setError(i18n.__('vaiu.topup.error.load_methods'));
        return [];
      }
    }
  };

  const fetchAcceptableCurrencies = async () => {
    try {
      const currencies = await platformApi.getAcceptableCurrencies();
      const toUpper = (list: string[] | undefined) =>
        (list ?? []).map((code) => String(code).toUpperCase());
      setAcceptableCurrencies({
        stripe: [...new Set([...toUpper(currencies.stripe), 'USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CHF'])],
        bank: toUpper(currencies.bank),
        paypal: toUpper(currencies.paypal),
        bitcoin: toUpper(currencies.bitcoin),
        ethereum: toUpper((currencies as { ethereum?: string[] }).ethereum)
      });
    } catch (error) {
      console.error('Failed to fetch acceptable currencies:', error);
    }
  };

  const getAvailablePaymentMethods = () => {
    return paymentMethods.filter(method => {
      if (method.type === 'stripe') {
        return stripeOk;
      } else if (method.type === 'plaid') {
        // NOTE: Only Stripe bank accounts (mapped as type 'plaid') are used
        // Plaid banks from getPaymentMethods() are excluded in fetchPaymentMethods()
        // This check includes Stripe Financial Connections banks which are mapped as 'plaid'
        return bankOk;
      } else if (method.type === 'paypal') {
        return paypalOffered;
      } else if (method.type === 'bitcoin') {
        return bitcoinOk;
      }
      return false;
    });
  };

  const getMethodIcon = (type: string) => {
    switch (type) {
      case 'stripe':
        return <CreditCard className="w-4 h-4" />;
      case 'plaid':
        return <Building2 className="w-4 h-4" />;
      case 'paypal':
        return <Wallet className="w-4 h-4 text-blue-600" />;
      case 'bitcoin':
        return <Bitcoin className="w-4 h-4 text-yellow-600" />;
      default:
        return <Wallet className="w-4 h-4" />;
    }
  };

  const getMethodDisplayName = (method: PaymentMethod) => {
    switch (method.type) {
      case 'stripe':
        return `${method.details.brand?.toUpperCase()} •••• ${method.details.last4}`;
      case 'plaid':
        // Check if this is a Stripe bank account (stored in details.source)
        const source = (method.details as any).source;
        const accountName = method.details.accountName || 'Bank Account';
        const accountType = method.details.accountType || '';
        const last4 = method.details.last4 || '****';
        if (source === 'stripe') {
          // Display Stripe bank account with source indicator
          return `${accountName}${accountType ? ` (${accountType})` : ''} •••• ${last4} (Stripe)`;
        } else {
          // Display Plaid bank account
          return `${accountName}${accountType ? ` (${accountType})` : ''}`;
        }
      case 'paypal':
        return 'PayPal';
      case 'bitcoin':
        return 'Bitcoin';
      default:
        return 'Unknown payment method';
    }
  };

  const getMethodTypeName = (type: string) => {
    switch (type) {
      case 'stripe':
        return 'Credit/Debit Card';
      case 'plaid':
        return 'Bank Account';
      case 'paypal':
        return 'PayPal';
      case 'bitcoin':
        return 'Bitcoin';
      default:
        return 'Payment Method';
    }
  };

  const selectSavedStripeMethod = (
    methods: PaymentMethod[],
    opts?: { stripePaymentMethodId?: string; last4?: string; brand?: string }
  ) => {
    const saved = methods.filter((m) => m.type === 'stripe' || m.type === 'plaid');
    const byPm = opts?.stripePaymentMethodId
      ? saved.find((m) => m.details?.paymentMethodId === opts.stripePaymentMethodId)
      : undefined;
    const byLast4 = opts?.last4
      ? saved.find(
          (m) =>
            m.details?.last4 === opts.last4 &&
            (!opts.brand || m.details?.brand === opts.brand)
        )
      : undefined;
    const chosen = byPm ?? byLast4 ?? saved.find((m) => m.isDefault) ?? saved[0];
    if (chosen?.id) {
      setSelectedMethodId(chosen.id);
    }
  };

  const finalizeCardPaymentSuccess = async (
    roundedTopUpAmount: number,
    newBalance: number,
    transactionId: string,
    fundingDetails: {
      source: string;
      amount: number;
      currency: string;
      paymentMethodType: string;
      last4?: string;
      brand?: string;
    },
    opts?: { stripePaymentMethodId?: string }
  ) => {
    setPaymentCompleted(true);
    setShowAddCardForm(false);
    setSelectedTab('card');
    setSelectedMethodId('');

    emitTopUpBalanceUpdate(
      roundedTopUpAmount,
      currency,
      currentBalance,
      transactionId,
      newBalance
    );

    if (onSuccess) {
      await onSuccess(newBalance, fundingDetails);
    }

    toast({
      title: i18n.__('vaiu.topup.success.payment'),
      description: i18n.__('vaiu.topup.success.charged')
        .replace('{currency}', currency)
        .replace('{amount}', roundedTopUpAmount.toFixed(currencyDecimals)),
    });

    try {
      const refreshed = await fetchPaymentMethods();
      selectSavedStripeMethod(refreshed, {
        stripePaymentMethodId: opts?.stripePaymentMethodId,
        last4: fundingDetails.last4,
        brand: fundingDetails.brand,
      });
    } catch {
      /* payment already succeeded */
    }
  };

  // Memoize handleCardAdded to prevent Stripe CardElement re-renders
  const handleCardAdded = useCallback(async (newMethod: PaymentMethod) => {
    try {
      const roundedTopUpAmount = tokenCreationChargeAmount;

      if (isGuest) {
        // For guest users, immediately charge the card after adding it
        console.log('💳 Guest card added, immediately processing payment...');
        setShowAddCardForm(false);
        setSelectedMethodId(newMethod.id);
        
        // Add the temporary method to the list for this session
        setPaymentMethods([newMethod]);
        
        // IMMEDIATELY charge the card for guests
        setLoading(true);
        try {
          // Process the payment right away using the correct method
          const result = await platformApi.topUpWithStripe({
            paymentMethodId: newMethod.id,
            amount: roundedTopUpAmount,
            currency: currency
          });
          
          if (result.success) {
            console.log('✅ Guest payment successful:', result);
            
            // Refresh balance from API to get accurate balance
            const balances = await platformApi.getBalances();
            const newBalance = balances[currency] || (currentBalance + roundedTopUpAmount);
            
            const fundingDetails = {
              source: 'stripe',
              amount: roundedTopUpAmount,
              currency,
              paymentMethodType: 'card',
              last4: result.paymentMethod?.last4 || newMethod.details?.last4,
              brand: result.paymentMethod?.brand || newMethod.details?.brand
            };

            await finalizeCardPaymentSuccess(
              roundedTopUpAmount,
              newBalance,
              result.transactionId || `topup_${Date.now()}`,
              fundingDetails,
              { stripePaymentMethodId: newMethod.id }
            );
          } else {
            throw new Error('Payment failed');
          }
        } catch (paymentError: any) {
          console.error('❌ Guest payment failed:', paymentError);
          setError(paymentError.message || i18n.__('vaiu.topup.error.generic'));
          toast({
            title: i18n.__('vaiu.topup.error.payment_failed'),
            description: paymentError.message || i18n.__('vaiu.topup.error.generic'),
            variant: "destructive"
          });
        } finally {
          setLoading(false);
        }
      } else {
        // For registered users, charge the newly entered card and save it in one backend call.
        setShowAddCardForm(false);
        setLoading(true);

        try {
          const stripePaymentMethodId = resolveStripePaymentMethodId(newMethod);
          const result = await platformApi.chargeAndSaveStripeCard({
            amount: roundedTopUpAmount,
            currency,
            paymentMethodId: stripePaymentMethodId,
          });

          if (!result?.success) {
            throw new Error('Payment failed');
          }

          const balances = await platformApi.getBalances();
          const newBalance = balances[currency] || roundToCurrency(currentBalance + roundedTopUpAmount);

          const fundingDetails = {
            source: 'stripe',
            amount: roundedTopUpAmount,
            currency,
            paymentMethodType: 'card',
            last4: result.paymentMethod?.last4 || newMethod.details?.last4,
            brand: result.paymentMethod?.brand || newMethod.details?.brand
          };

          await finalizeCardPaymentSuccess(
            roundedTopUpAmount,
            newBalance,
            result.transactionId || `topup_${Date.now()}`,
            fundingDetails,
            { stripePaymentMethodId }
          );
        } catch (paymentError: any) {
          console.error('❌ Registered card charge/save failed:', paymentError);
          setError(paymentError.message || i18n.__('vaiu.topup.error.generic'));
          toast({
            title: i18n.__('vaiu.topup.error.payment_failed'),
            description: paymentError.message || i18n.__('vaiu.topup.error.generic'),
            variant: "destructive"
          });
        } finally {
          setLoading(false);
        }
      }
    } catch (error) {
      console.error('Failed to process new card:', error);
      toast({
        title: i18n.__('vaiu.topup.error.payment_failed'),
        description: i18n.__('vaiu.topup.error.card'),
        variant: "destructive",
      });
    }
  }, [isGuest, tokenCreationChargeAmount, currency, currencyDecimals, currentBalance, amounts.minimumTopUp, onSuccess, toast]);
  
  // Memoize guest card form cancel callback to prevent Stripe CardElement re-renders
  // For guests, cancel doesn't close TopUpBalance - it's a no-op since other methods are always visible
  const handleGuestCardFormCancel = useCallback(() => {
    // No-op: Other payment methods are always visible, so cancel doesn't need to do anything
    // The card form will remain visible and user can use other methods below
  }, []);

  // Memoize disabled state for guest card form to prevent re-renders
  const guestCardFormDisabled = useMemo(() => {
    return loading || topUpAmount < amounts.minimumTopUp || !meetsStripeMinimum(topUpAmount, currency);
  }, [loading, topUpAmount, amounts.minimumTopUp, currency]);

  // Memoize error handler for digital wallet to prevent re-renders
  const handleDigitalWalletError = useCallback((err: Error) => {
    setError(err.message);
  }, []);

  // Note: handlePayPalSelect will be recreated when handleTopUp changes
  // This is acceptable as handleTopUp is only called from here and PayPal button
  const handlePayPalSelectRef = useRef<() => void>();

  // Memoize ATM error handler to prevent re-renders
  const handleATMError = useCallback((err: string) => {
    setError(err);
  }, []);

  // Memoize crypto error handlers to prevent re-renders
  const handleCryptoError = useCallback((err: string) => {
    setError(err);
  }, []);

  // Memoize crypto success handlers to prevent re-renders
  const handleBitcoinSuccess = useCallback((newBalance: number, fundingDetails?: any) => {
    const amountToEmit = isTokenCreationFlow ? tokenCreationChargeAmount : topUpAmount;
    emitTopUpBalanceUpdate(
      amountToEmit,
      currency,
      currentBalance,
      fundingDetails?.transactionId || `btc_${Date.now()}`,
      newBalance
    );
    onSuccess(newBalance, fundingDetails);
  }, [topUpAmount, tokenCreationChargeAmount, isTokenCreationFlow, currency, currentBalance, onSuccess]);

  const handleEthereumSuccess = useCallback((newBalance: number, fundingDetails?: any) => {
    const amountToEmit = isTokenCreationFlow ? tokenCreationChargeAmount : topUpAmount;
    emitTopUpBalanceUpdate(
      amountToEmit,
      currency,
      currentBalance,
      fundingDetails?.transactionId || `eth_${Date.now()}`,
      newBalance
    );
    onSuccess(newBalance, fundingDetails);
  }, [topUpAmount, tokenCreationChargeAmount, isTokenCreationFlow, currency, currentBalance, onSuccess]);

  const handleTopUp = async () => {
    if (!selectedMethodId || selectedMethodId === 'add-new-card') {
      setError(i18n.__('vaiu.topup.error.select_method'));
      return;
    }

    // PayPal minimum is > 0, Stripe/card minimum is amounts.minimumTopUp (e.g., $0.50 for USD)
    const selectedMethod = paymentMethods.find(m => m.id === selectedMethodId);
    const isPayPal = selectedMethod?.type === 'paypal';

    // PayPal redirect only from the PayPal tab (guest UI uses explicit Continue to PayPal)
    if (isPayPal && !isGuest && selectedTab !== 'paypal') {
      setError('Please use the PayPal tab to pay with PayPal.');
      return;
    }
    const chargeAmount = isTokenCreationFlow ? tokenCreationChargeAmount : roundToCurrency(topUpAmount);
    const effectiveMinimum = isPayPal 
      ? Math.max(isTokenCreationFlow ? chargeAmount : amounts.shortfall, 0.01) // PayPal: anything > 0
      : amounts.minimumTopUp; // Stripe/card: Stripe minimum (e.g., $0.50 for USD)

    if (chargeAmount < effectiveMinimum || chargeAmount <= 0) {
      const formattedMinimum = await currencyService.formatCurrency(effectiveMinimum, currency);
      setError(isGuest 
        ? i18n.__('vaiu.topup.error.minimum_payment').replace('{amount}', formattedMinimum)
        : i18n.__('vaiu.topup.error.minimum_topup').replace('{amount}', formattedMinimum));
      return;
    }

    setLoading(true);
    setError(null);

    let paypalFundingTab: Window | null = null;

    try {
      const selectedMethod = paymentMethods.find(m => m.id === selectedMethodId);
      if (!selectedMethod) {
        throw new Error(i18n.__('vaiu.topup.error.method_not_found'));
      }

      // Use the selected charge amount. In token-creation flow this is the full token reserve.
      const roundedTopUpAmount = chargeAmount;

      let result;
      if (selectedMethod.type === 'stripe') {
        result = await platformApi.topUpWithStripe({
          amount: roundedTopUpAmount,
          currency,
          paymentMethodId: selectedMethodId
        });
      } else if (selectedMethod.type === 'plaid') {
        result = await platformApi.topUpWithACH({
          amount: roundedTopUpAmount,
          currency,
          paymentMethodId: selectedMethodId
        });
      } else if (selectedMethod.type === 'paypal') {
        const embedded = isFramedWindow();
        const sessionId = embedded ? createPayPalFundingSessionId() : '';
        if (embedded) {
          paypalFundingTab = openPayPalFundingTab();
          try {
            sessionStorage.setItem('paypalFundingSessionId', sessionId);
          } catch {
            /* ignore */
          }
        }

        let returnUrl: string;
        let contextData = '';
        
        if (context?.type === 'token-creation' && context.returnUrl) {
          returnUrl = context.returnUrl;
          
          // Encode topup data in the context
          const params = new URLSearchParams({
            topupAmount: roundedTopUpAmount.toString(),
            topupCurrency: currency,
            ...(context.tokenAmount && { tokenAmount: context.tokenAmount.toString() }),
            ...(context.tokenCurrency && { tokenCurrency: context.tokenCurrency }),
            ...(context.tokenMessage && { tokenMessage: context.tokenMessage }),
            ...(context.tokenKey && { tokenKey: context.tokenKey })
          });
          contextData = `topup:${params.toString()}`;
          
          console.log('💳 [TopUpBalance] PayPal return URL for topup:', returnUrl);
          console.log('💳 [TopUpBalance] PayPal context data:', contextData);
          console.log('💳 [TopUpBalance] Context returnUrl received:', context.returnUrl);
          console.log('💳 [TopUpBalance] Current window.location.href:', window.location.href);
          console.log('💳 [TopUpBalance] Current window.location.pathname:', window.location.pathname);
          
          // DEBUG: Parse and log the return URL to verify it's correct
          try {
            const returnUrlObj = new URL(returnUrl);
            console.log('💳 [TopUpBalance] Parsed return URL:', {
              origin: returnUrlObj.origin,
              pathname: returnUrlObj.pathname,
              search: returnUrlObj.search,
              href: returnUrlObj.href
            });
          } catch (e) {
            console.error('💳 [TopUpBalance] Failed to parse return URL:', e);
          }
          
          // Mobile debug - extract pathname from returnUrl for display
          try {
            const returnUrlObj = new URL(returnUrl);
            toast({
              title: i18n.__('vaiu.topup.toast.paypal_redirect'),
              description: i18n.__('vaiu.topup.toast.returning_to', { path: returnUrlObj.pathname + returnUrlObj.search }),
            });
          } catch (e) {
            toast({
              title: i18n.__('vaiu.topup.toast.paypal_redirect'),
              description: i18n.__('vaiu.topup.toast.returning_to', { path: returnUrl }),
            });
          }
        } else {
          returnUrl = resolvePayPalCallbackBaseUrl();
        }
        
        result = await platformApi.topUpWithPayPal({
          amount: roundedTopUpAmount,
          currency,
          paymentMethodId: selectedMethodId,
          returnUrl,
          context: contextData,
          sessionId: sessionId || undefined,
          paypalTab: embedded,
        });
        
        const approvalURL = paypalApprovalUrl(result);
        if (approvalURL) {
          const absoluteApproval = toAbsolutePayPalUrl(approvalURL);
          if (context?.type === 'token-creation') {
            const storedReturnUrl = resolvePayPalCallbackBaseUrl({
              returnUrl: context.returnUrl,
            });
            sessionStorage.setItem('pendingTokenCreation', JSON.stringify({
              amount: context.tokenAmount || roundedTopUpAmount,
              currency: context.tokenCurrency || currency,
              message: context.tokenMessage,
              tokenKey: context.tokenKey,
              returnUrl: storedReturnUrl,
              timestamp: Date.now()
            }));
            try {
              const returnUrlObj = new URL(storedReturnUrl);
              sessionStorage.setItem('paypalOriginalPath', returnUrlObj.pathname + returnUrlObj.search);
            } catch {
              sessionStorage.setItem('paypalOriginalPath', window.location.pathname + window.location.search);
            }
          } else {
            sessionStorage.setItem('paypalOriginalPath', window.location.pathname);
          }

          toast({
            title: i18n.__('vaiu.topup.success.paypal_redirect'),
            description: i18n.__('vaiu.topup.success.paypal_description'),
          });
          if (embedded) {
            if (paypalFundingTab && !paypalFundingTab.closed) {
              navigatePayPalFundingTab(paypalFundingTab, absoluteApproval);
              paypalFundingTab = null;
              return;
            }
            setPaypalApprovalHref(absoluteApproval);
            setError('This embed must allow popups so PayPal can open in a new tab. You can continue in this window instead.');
            return;
          }
          goToPayPal(absoluteApproval);
          return;
        }
      } else if (selectedMethod.type === 'bitcoin') {
        // Bitcoin transactions require manual handling
        toast({
          title: i18n.__('vaiu.topup.toast.bitcoin_initiated'),
          description: i18n.__('vaiu.topup.toast.bitcoin_complete'),
        });
        
        // For now, simulate successful Bitcoin transaction
        result = {
          success: true,
          transactionId: `btc_${Date.now()}`,
          amount: roundedTopUpAmount,
          currency
        };
        
        // In a real implementation, this would:
        // 1. Generate a Bitcoin address for the user to send to
        // 2. Monitor the blockchain for the transaction
        // 3. Credit the user's account once confirmed
      } else {
        throw new Error('Unsupported payment method type');
      }

      // Refresh balance from API to get accurate balance
      const balances = await platformApi.getBalances();
      const newBalance = balances[currency] || roundToCurrency(currentBalance + roundedTopUpAmount);

      const formattedAmount = await currencyService.formatCurrency(roundedTopUpAmount, currency);

      // Emit balance update event with actual balance from API
      emitTopUpBalanceUpdate(
        roundedTopUpAmount,
        currency,
        currentBalance,
        result.transactionId || `topup_${Date.now()}`,
        newBalance // Pass actual fetched balance
      );

      toast({
        title: isGuest ? i18n.__('vaiu.topup.success.payment') : i18n.__('vaiu.topup.success.topup'),
        description: isGuest 
          ? i18n.__('vaiu.topup.success.paid_amount').replace('{amount}', formattedAmount).replace('{method}', getMethodDisplayName(selectedMethod))
          : i18n.__('vaiu.topup.success.balance_added').replace('{amount}', formattedAmount).replace('{method}', getMethodDisplayName(selectedMethod)),
      });

      // Prepare funding details for audit trail
      const fundingDetails = {
        source: selectedMethod.type,
        amount: roundedTopUpAmount,
        currency,
        paymentMethodType: selectedMethod.type,
        last4: result.paymentMethod?.last4 || selectedMethod.details?.last4,
        brand: result.paymentMethod?.brand || selectedMethod.details?.brand
      };

      // Notify parent component of success with refreshed balance and funding details
      onSuccess(newBalance, fundingDetails);

    } catch (error: any) {
      closePayPalFundingTab(paypalFundingTab);
      setError(error.message || (isGuest ? i18n.__('vaiu.topup.error.generic') : i18n.__('vaiu.topup.error.balance')));
      toast({
        title: isGuest ? i18n.__('vaiu.topup.error.payment_failed') : i18n.__('vaiu.topup.error.topup_failed'),
        description: error.message || (isGuest ? i18n.__('vaiu.topup.error.generic') : i18n.__('vaiu.topup.error.balance')),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Create handlePayPalSelect after handleTopUp is defined
  // Use useMemo to create it only when handleTopUp changes
  const handlePayPalSelect = useMemo(() => {
    return () => {
      setSelectedMethodId('paypal-funding');
    };
  }, []);

  const handleATMSuccess = async (tokenId: string, authCode: string) => {
    setLoading(true);
    try {
      const roundedTopUpAmount = parseFloat(topUpAmount.toFixed(currencyDecimals));
      
      // ATM withdrawal is processed by backend via CardToTokenFlow
      // The backend handles the funding automatically, so we just need to refresh balance
      const balances = await platformApi.getBalances();
      const newBalance = balances[currency] || currentBalance;
      const actualTopUpAmount = newBalance - currentBalance;

      const formattedAmount = await currencyService.formatCurrency(roundedTopUpAmount, currency);

      toast({
        title: isGuest ? i18n.__('vaiu.topup.success.payment') : i18n.__('vaiu.topup.success.topup'),
        description: isGuest 
          ? i18n.__('vaiu.topup.success.atm_paid').replace('{amount}', formattedAmount)
          : i18n.__('vaiu.topup.success.atm_added').replace('{amount}', formattedAmount),
      });

      // Emit balance update event with actual balance from API
      emitTopUpBalanceUpdate(
        actualTopUpAmount > 0 ? actualTopUpAmount : roundedTopUpAmount,
        currency,
        currentBalance,
        `atm_${Date.now()}`,
        newBalance // Pass actual fetched balance
      );

      // Prepare funding details
      const fundingDetails = {
        source: 'atm',
        amount: actualTopUpAmount > 0 ? actualTopUpAmount : roundedTopUpAmount,
        currency,
        paymentMethodType: 'atm',
        authCode: authCode
      };

      // Notify parent component
      onSuccess(newBalance, fundingDetails);
    } catch (error: any) {
      setError(error.message || i18n.__('vaiu.topup.error.atm_failed'));
      toast({
        title: i18n.__('vaiu.topup.error.payment_failed'),
        description: error.message || i18n.__('vaiu.topup.error.atm'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Memoize GuestPaymentUI JSX to prevent Stripe CardElement re-renders
  // Extract as useMemo to prevent recreation on every render
  const guestPaymentUI = useMemo(() => {
    const hasStripeMinimum = meetsStripeMinimum(topUpAmount, currency);
    const stripeMinimumMessage = getStripeMinimumMessage(currency);
    
    return (
      <div className="w-full max-w-full min-w-0 space-y-4">
        {/* Payment Amount Display */}
      <div className="space-y-2">
          <Label>{i18n.__('vaiu.topup.payment_amount')}</Label>
        <Input
          type="text"
          value={`${roundToCurrency(topUpAmount)} ${currency}`}
          readOnly
          className="text-lg font-bold bg-gray-100"
        />
      </div>

        {!hasStripeMinimum && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{stripeMinimumMessage}</AlertDescription>
          </Alert>
        )}

        {/* Other Payment Methods - Filtered by currency support */}
        <div className="space-y-4">
          {/* Digital Wallets Section - Only show if Stripe supports this currency */}
          {stripeOk && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Smartphone className="w-4 h-4 text-muted-foreground" />
                <h4 className="font-semibold text-xs uppercase tracking-wide text-muted-foreground">{i18n.__('vaiu.topup.digital_wallets')}</h4>
              </div>
              <PaymentMethodDigitalWallet
                amount={topUpAmount}
                currency={currency}
                onPaymentSuccess={handleDigitalWalletPayment}
                onPaymentError={handleDigitalWalletError}
                disabled={loading || topUpAmount < amounts.minimumTopUp || !hasStripeMinimum}
                showSeparator={false}
              />
            </div>
          )}

        {/* Card form before PayPal (guest UI has no tabs — same priority as authenticated Card tab) */}
        {stripeOk && (
          <PaymentMethodCardForm
            amount={topUpAmount}
            currency={currency}
            onSubmit={handleCardAdded}
            onCancel={handleGuestCardFormCancel}
            isGuest={true}
            disabled={guestCardFormDisabled}
            showSeparator={false}
            submitLabel={chargeCardLabel}
            loadingLabel={chargingCardLabel}
          />
        )}

          {/* ATM Method (Virtual Digital ATM) - Only show if card-to-token is enabled and Stripe supports currency */}
          {isCardToTokenEnabled() && stripeOk && (
            <PaymentMethodATM
              amount={topUpAmount}
              currency={currency}
              onSuccess={handleATMSuccess}
              onError={handleATMError}
              onCancel={onCancel}
              disabled={loading || topUpAmount < amounts.minimumTopUp}
            />
          )}

          {/* PayPal — after Stripe/card paths */}
          {paypalOffered && (
            <div className="space-y-3 border-t border-dashed border-muted-foreground/30 pt-4">
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-muted-foreground" />
                <h4 className="font-semibold text-xs uppercase tracking-wide text-muted-foreground">
                  {i18n.__('vaiu.topup.tabs.paypal')}
                </h4>
              </div>
              <PaymentMethodPayPal
                amount={topUpAmount}
                currency={currency}
                onSelect={handlePayPalSelect}
                onPay={() => {
                  setSelectedMethodId('paypal-funding');
                  handleTopUp();
                }}
                disabled={loading || topUpAmount < amounts.shortfall || topUpAmount <= 0}
                isSelected={selectedMethodId === 'paypal-funding'}
                approvalHref={paypalApprovalHref}
              />
            </div>
          )}
        </div>

        {(bitcoinOk || ethereumOk) && (
        <div className="space-y-4 pt-2">
          <div className="flex items-center gap-2">
            <Wallet className="w-4 h-4 text-muted-foreground" />
            <h4 className="font-semibold text-xs uppercase tracking-wide text-muted-foreground">{i18n.__('vaiu.topup.crypto')}</h4>
          </div>
          
          {bitcoinOk && (
            <PaymentMethodBitcoin
              amount={topUpAmount}
              currency={currency}
              onSuccess={handleBitcoinSuccess}
              onError={handleCryptoError}
              onCancel={onCancel}
              disabled={loading || topUpAmount < amounts.minimumTopUp}
              paymentMethods={paymentMethods}
              showSeparator={false}
            />
          )}

          {ethereumOk && (
            <PaymentMethodEthereum
              amount={topUpAmount}
              currency={currency}
              onSuccess={handleEthereumSuccess}
              onError={handleCryptoError}
              onCancel={onCancel}
              disabled={loading || topUpAmount < amounts.minimumTopUp}
              showSeparator={bitcoinOk}
            />
          )}
        </div>
        )}
      </div>
    );
  }, [
    topUpAmount, 
    currency, 
    loading, 
    amounts.minimumTopUp, 
    acceptableCurrencies, 
    handleDigitalWalletPayment, 
    handleDigitalWalletError,
    handleATMSuccess, 
    handleATMError,
    handleCardAdded, 
    handleGuestCardFormCancel, 
    guestCardFormDisabled, 
    handlePayPalSelect,
    handleBitcoinSuccess,
    handleEthereumSuccess,
    handleCryptoError,
    onCancel, 
    paymentMethods, 
    selectedMethodId,
    paypalApprovalHref,
    paypalOffered,
    stripeOk,
    bitcoinOk,
    ethereumOk,
  ]);

  const availableMethods = getAvailablePaymentMethods();
  const savedCardMethods = availableMethods.filter(
    (m) => m.type === 'stripe'
  );
  const savedBankMethods = availableMethods.filter(
    (m) => m.type === 'stripe_bank' || m.type === 'plaid'
  );
  const hasSavedCardMethods = savedCardMethods.length > 0;
  const showCardEntryForm = showAddCardForm || !hasSavedCardMethods;

  if (!isOpen) return null;

  const fundingSourceSelector = isTokenCreationFlow ? (
    <div className="space-y-4">
      <div className="space-y-1">
        <h3 className="text-sm font-semibold text-foreground">{i18n.__('vaiu.topup.funding.question')}</h3>
      </div>
      <div className="-mx-1 flex snap-x snap-mandatory items-stretch gap-3 overflow-x-auto px-1 pb-2">
        <button
          type="button"
          onClick={() => selectFundingSource('balance')}
          className={fundingOutlineTileClass(activeFundingSource === 'balance')}
        >
          <p className="text-[11px] font-semibold text-blue-600">
            {i18n.__('vaiu.topup.funding.balance_label')}
          </p>
          <p className="mt-1 text-[26px] font-extrabold leading-none tracking-tight text-slate-900">
            {tokenCurrencyBalance.toFixed(amountDecimals)}
          </p>
          <p className="mt-1 text-base font-extrabold tracking-wide text-slate-900">{currency}</p>
        </button>

        {stripeOk && savedCardMethods.map((method) => {
          const methodId = method.id || method.key || method._id || '';
          const isSelected = activeFundingSource === 'card' && selectedMethodId === methodId;
          const cardBrand = getCardBrandPresentation(method.details?.brand);
          const cardCaption = method.isDefault
            ? i18n.__('vaiu.topup.funding.default_card')
            : i18n.__('vaiu.topup.funding.saved_card');
          return (
            <button
              key={methodId}
              type="button"
              aria-label={`${cardBrand.label} ${cardCaption} ${method.details?.last4 || ''}`}
              onClick={() => {
                selectFundingSource('card');
                setSelectedMethodId(methodId);
                setShowAddCardForm(false);
              }}
              className={fundingBrandTileClass(cardBrand.className, isSelected)}
            >
              <div className="flex items-start justify-between">
                <span className="text-[11px] font-semibold uppercase">{cardBrand.label}</span>
                {cardBrand.logo ? (
                  <img src={cardBrand.logo} alt="" className="h-4 max-w-10 object-contain" />
                ) : (
                  <CreditCard className="h-4 w-4" />
                )}
              </div>
              <div className="mt-5 text-sm font-semibold tracking-widest">•••• {method.details?.last4 || '0000'}</div>
            </button>
          );
        })}
        {stripeOk && (
          <button
            type="button"
            onClick={() => {
              selectFundingSource('card');
              setSelectedMethodId('add-new-card');
              setShowAddCardForm(true);
            }}
            className={`flex ${FUNDING_TILE} flex-col items-center justify-center gap-1 border-2 border-dashed border-muted-foreground/40 bg-background text-muted-foreground hover:border-blue-400 hover:text-blue-600`}
            aria-label={i18n.__('vaiu.topup.add_new_card')}
          >
            <Plus className="h-6 w-6" />
            <span className="text-xs font-medium">{i18n.__('vaiu.topup.add_new_card')}</span>
          </button>
        )}

        {bankOk && savedBankMethods.map((method) => {
          const methodId = method.id || method.key || method._id || '';
          const isSelected = activeFundingSource === 'bank' && selectedMethodId === methodId;
          const bankBrand = getBankBrandPresentation(method.details?.accountName);
          return (
            <button
              key={methodId}
              type="button"
              aria-label={`${bankBrand.label} ${method.details?.last4 ? method.details.last4 : i18n.__('vaiu.topup.funding.linked_account')}`}
              onClick={() => {
                selectFundingSource('bank');
                setSelectedMethodId(methodId);
                setShowAddCardForm(false);
              }}
              className={fundingBrandTileClass(bankBrand.className, isSelected)}
            >
              <div className="flex items-start justify-between gap-2">
                <Building2 className="h-4 w-4" />
                {bankBrand.logo ? (
                  <img src={bankBrand.logo} alt="" className="h-5 max-w-16 object-contain" />
                ) : null}
              </div>
              <p className="mt-3 truncate text-sm font-semibold">{bankBrand.label}</p>
              <p className={`mt-0.5 text-xs ${bankBrand.mutedClassName}`}>
                {method.details?.last4 ? `•••• ${method.details.last4}` : i18n.__('vaiu.topup.funding.linked_account')}
              </p>
            </button>
          );
        })}
        {bankOk && (
          <button
            type="button"
            onClick={() => {
              const rawReturn = context?.returnUrl || `${window.location.pathname}${window.location.search}`;
              let returnPath = '/';
              try {
                if (rawReturn.startsWith('http://') || rawReturn.startsWith('https://')) {
                  const parsed = new URL(rawReturn);
                  returnPath = `${parsed.pathname}${parsed.search}`;
                } else if (rawReturn.startsWith('/') && !rawReturn.startsWith('//')) {
                  returnPath = rawReturn;
                }
              } catch {
                returnPath = '/';
              }
              navigate(`/payment-methods/add?method=bank&returnUrl=${encodeURIComponent(returnPath)}`);
            }}
            className={`flex ${FUNDING_TILE} flex-col items-center justify-center gap-1 border-2 border-dashed border-muted-foreground/40 bg-background text-muted-foreground hover:border-blue-400 hover:text-blue-600`}
            aria-label={i18n.__('vaiu.topup.add_bank_account')}
          >
            <Plus className="h-6 w-6" />
            <span className="text-xs font-medium">{i18n.__('vaiu.topup.add_bank_account')}</span>
          </button>
        )}

        {paypalOffered && (
          <button
            type="button"
            onClick={() => selectFundingSource('paypal')}
            className={fundingOutlineTileClass(activeFundingSource === 'paypal')}
          >
            <Globe className="h-4 w-4" />
            <p className="mt-3 text-sm font-semibold">PayPal</p>
            <p className="mt-1 text-xs text-muted-foreground">{i18n.__('vaiu.topup.funding.paypal_checkout')}</p>
          </button>
        )}
        {stripeOk && (
          <button
            type="button"
            onClick={() => selectFundingSource('wallet')}
            className={fundingOutlineTileClass(activeFundingSource === 'wallet')}
          >
            <Smartphone className="h-4 w-4" />
            <p className="mt-3 text-sm font-semibold">{i18n.__('vaiu.topup.funding.digital_wallet')}</p>
            <p className="mt-1 text-xs text-muted-foreground">On this device</p>
          </button>
        )}
        {bitcoinOk && (
          <button
            type="button"
            onClick={() => selectFundingSource('bitcoin')}
            className={fundingOutlineTileClass(activeFundingSource === 'bitcoin')}
          >
            <Bitcoin className="h-4 w-4" />
            <p className="mt-3 text-sm font-semibold">Bitcoin</p>
          </button>
        )}
        {ethereumOk && (
          <button
            type="button"
            onClick={() => selectFundingSource('ethereum')}
            className={fundingOutlineTileClass(activeFundingSource === 'ethereum')}
          >
            <Globe className="h-4 w-4" />
            <p className="mt-3 text-sm font-semibold">Ethereum</p>
          </button>
        )}
      </div>

      {activeFundingSource === 'balance' && (
        <div className="space-y-3 rounded-lg border border-border bg-background p-3">
          <div className="rounded-lg border-2 border-blue-500 bg-blue-50 p-3 text-blue-950">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[11px] font-semibold text-blue-600">{i18n.__('vaiu.topup.funding.balance_label')}</p>
                <p className="text-[26px] font-extrabold leading-none tracking-tight text-slate-900">
                  {tokenCurrencyBalance.toFixed(amountDecimals)}
                </p>
                <p className="mt-1 text-base font-extrabold tracking-wide text-slate-900">{currency}</p>
              </div>
              {hasTokenCreationAmount ? (
                <div className="text-right text-sm font-semibold">
                  {tokenCreationChargeAmount.toFixed(amountDecimals)} {currency}
                </div>
              ) : null}
            </div>
            {!hasTokenCreationAmount ? (
              <p className="mt-2 text-xs text-muted-foreground">{i18n.__('vaiu.topup.funding.enter_amount')}</p>
            ) : balanceCanFundToken ? (
              <p className="mt-2 text-xs text-green-700">{i18n.__('vaiu.topup.funding.ready_from_balance')}</p>
            ) : (
              <p className="mt-2 text-xs font-medium text-red-600">{i18n.__('vaiu.topup.funding.insufficient_balance')}</p>
            )}
          </div>
          {!hasTokenCreationAmount ? (
            <p className="mt-3 text-xs text-muted-foreground">{i18n.__('vaiu.topup.funding.enter_amount')}</p>
          ) : !balanceCanFundToken ? (
            <Alert variant="destructive" className="mt-3">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {i18n.__('vaiu.topup.funding.insufficient_detail')}
              </AlertDescription>
            </Alert>
          ) : (
            <div className="mt-3 flex gap-2">
              <Button type="button" variant="outline" className="flex-1" onClick={onCancel}>
                {i18n.__('common.cancel')}
              </Button>
              <Button type="submit" form={balanceSubmitFormId} className="flex-1" disabled={!hasTokenCreationAmount}>
                {i18n.__('vaiu.topup.funding.create_from_balance')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  ) : null;

  const mainContent = (
    <Card className="w-full max-w-full min-w-0 overflow-hidden border-2 border-blue-200 bg-blue-50/50">
      <CardHeader className="pb-4 px-4 sm:px-6">
        <div
          className={`flex items-center gap-2 min-w-0 ${
            isTokenCreationFlow ? 'justify-end' : 'justify-between'
          }`}
        >
          {!isTokenCreationFlow && (
            <CardTitle className="flex items-center gap-2 min-w-0 text-base sm:text-lg">
              <DollarSign className="w-5 h-5 shrink-0 text-blue-600" />
              <span className="truncate">{topUpTitle}</span>
            </CardTitle>
          )}
          <Button variant="ghost" size="sm" onClick={onCancel} className="shrink-0 p-2" aria-label={i18n.__('common.cancel')}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="min-w-0 px-4 sm:px-6">
        {/* Debug Panel for Mobile Testing */}
        {debugInfo.length > 0 && (
          <Alert className="mb-4 bg-blue-50 border-blue-200">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <div className="text-xs space-y-1">
                <strong>Debug Info:</strong>
                {debugInfo.map((info, idx) => (
                  <div key={idx}>{info}</div>
                ))}
              </div>
            </AlertDescription>
          </Alert>
        )}
        
        {paymentCompleted ? (
          <Alert className="mb-4 border-green-200 bg-green-50">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertDescription>
              {i18n.__('vaiu.topup.success.payment')} — {i18n.__('vaiu.topup.success.charged')
                .replace('{currency}', currency)
                .replace('{amount}', roundToCurrency(topUpAmount).toFixed(currencyDecimals))}
            </AlertDescription>
          </Alert>
        ) : isGuest ? (
          guestPaymentUI
        ) : (
          // Tabbed UI for authenticated users
          <div className="space-y-4">
            {fundingSourceSelector}

            {activeFundingSource !== 'balance' && (
            <div className="space-y-2">
              <Label htmlFor="topup-amount">{amountLabel}</Label>
              <Input 
                id="topup-amount" 
                type="number" 
                min={isTokenCreationFlow ? tokenCreationChargeAmount : amounts.minimumTopUp}
                step="0.01"
                value={topUpAmountInput} 
                readOnly={isTokenCreationFlow}
                className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                onChange={(e) => {
                  if (isTokenCreationFlow) return;
                  const inputValue = e.target.value;
                  setTopUpAmountInput(inputValue);
                  // Only update numeric value if input is valid
                  const numValue = parseFloat(inputValue);
                  if (!isNaN(numValue) && inputValue !== '') {
                    setTopUpAmount(numValue);
                  } else if (inputValue === '' || inputValue === '.') {
                    // Allow empty or just decimal point while typing
                    setTopUpAmount(0);
                  }
                }}
                onBlur={(e) => {
                  if (isTokenCreationFlow) {
                    const chargeAmount = tokenCreationChargeAmount;
                    setTopUpAmount(chargeAmount);
                    setTopUpAmountInput(chargeAmount.toFixed(currencyDecimals));
                    return;
                  }
                  // On blur, ensure value is properly rounded and at least minimum
                  // PayPal minimum is > 0, Stripe/card minimum is amounts.minimumTopUp (e.g., $0.50 for USD)
                  const numValue = parseFloat(e.target.value);
                  
                  // Check if PayPal is selected or if user might be selecting PayPal
                  // (don't enforce Stripe minimum if amount is > 0 and PayPal is available)
                  const isPayPal = selectedMethodId === 'paypal-funding';
                  const paypalAvailable = paypalOffered;
                  
                  // For PayPal: minimum is > 0 (use shortfall or 0.01, whichever is higher)
                  // For Stripe/card: minimum is amounts.minimumTopUp (e.g., $0.50 for USD)
                  const paypalMinimum = Math.max(amounts.shortfall, 0.01);
                  const effectiveMinimum = isPayPal 
                    ? paypalMinimum // PayPal: anything > 0
                    : amounts.minimumTopUp; // Stripe/card: Stripe minimum (e.g., $0.50 for USD)
                  
                  if (isNaN(numValue) || numValue <= 0) {
                    // Invalid or zero amount - enforce minimum based on selected method
                    const finalValue = effectiveMinimum;
                    setTopUpAmount(finalValue);
                    setTopUpAmountInput(finalValue.toString());
                  } else if (numValue < effectiveMinimum) {
                    // Amount is below minimum for selected payment method
                    // If PayPal is available and amount is >= PayPal minimum, don't change it
                    // (user might be about to select PayPal)
                    if (paypalAvailable && numValue >= paypalMinimum && !isPayPal) {
                      // User might select PayPal, preserve the amount
                      const roundedValue = roundToCurrency(numValue);
                      setTopUpAmount(roundedValue);
                      setTopUpAmountInput(roundedValue.toString());
                    } else {
                      // Enforce minimum for selected method
                      const finalValue = effectiveMinimum;
                      setTopUpAmount(finalValue);
                      setTopUpAmountInput(finalValue.toString());
                    }
                  } else {
                    // Valid amount, just round it
                    const roundedValue = roundToCurrency(numValue);
                    setTopUpAmount(roundedValue);
                    setTopUpAmountInput(roundedValue.toString());
                  }
                }}
              />
              {!isTokenCreationFlow && amounts.shortfall > 0 && (
                <p className="text-sm text-muted-foreground">
                  {i18n.__('vaiu.topup.shortfall')} <CurrencyDisplay amount={amounts.shortfall} currency={currency} currencyService={currencyService} />
                </p>
              )}
            </div>
            )}

            {activeFundingSource !== 'balance' && (
            <Tabs
              value={selectedTab}
              onValueChange={(v) => {
                userPickedPaymentTabRef.current = true;
                setSelectedTab(v);
                if (v === 'card' && selectedMethodId === 'paypal-funding') {
                  const savedId = pickDefaultSavedCardMethodId();
                  if (savedId) {
                    setSelectedMethodId(savedId);
                    setShowAddCardForm(false);
                  } else {
                    setSelectedMethodId('add-new-card');
                    setShowAddCardForm(true);
                  }
                }
              }}
            >
              {!lockedPaymentTab && (
                <TabsList
                  className="grid h-auto w-full max-w-full gap-0.5 p-1"
                  style={{
                    gridTemplateColumns: `repeat(${(() => {
                      let count = 0;
                      if (amounts.shortfall > 0 && isCardToTokenEnabled() && stripeOk) count++;
                      if (stripeOk) count++;
                      if (stripeOk) count++;
                      if (paypalOffered) count++;
                      return count || 1;
                    })()}, minmax(0, 1fr))`,
                  }}
                >
                  {/* ATM Tab - Only show if card-to-token enabled, shortfall exists, and Stripe supports currency */}
                  {amounts.shortfall > 0 && isCardToTokenEnabled() && stripeOk && (
                    <TabsTrigger value="atm" className="min-w-0 whitespace-normal px-1 py-1.5 text-[11px] leading-tight sm:px-3 sm:text-sm sm:whitespace-nowrap">
                      <CreditCard className="mb-0.5 h-3.5 w-3.5 shrink-0 sm:mb-0 sm:mr-2 sm:h-4 sm:w-4" />
                      <span className="truncate">{i18n.__('vaiu.topup.tabs.atm')}</span>
                    </TabsTrigger>
                  )}
                  {/* Card Tab - Only show if Stripe supports currency */}
                  {stripeOk && (
                    <TabsTrigger value="card" className="min-w-0 whitespace-normal px-1 py-1.5 text-[11px] leading-tight sm:px-3 sm:text-sm sm:whitespace-nowrap">
                      <CreditCard className="mb-0.5 h-3.5 w-3.5 shrink-0 sm:mb-0 sm:mr-2 sm:h-4 sm:w-4" />
                      <span className="truncate">{cardTabLabel}</span>
                    </TabsTrigger>
                  )}
                  {/* Digital Wallet Tab - Only show if Stripe supports currency (card rails; before PayPal) */}
                  {stripeOk && (
                    <TabsTrigger value="digital-wallet" className="min-w-0 whitespace-normal px-1 py-1.5 text-[11px] leading-tight sm:px-3 sm:text-sm sm:whitespace-nowrap">
                      <Smartphone className="mb-0.5 h-3.5 w-3.5 shrink-0 sm:mb-0 sm:mr-2 sm:h-4 sm:w-4" />
                      <span className="truncate">{i18n.__('vaiu.topup.tabs.wallet')}</span>
                    </TabsTrigger>
                  )}
                  {/* PayPal Tab - Only show if PayPal supports currency */}
                  {paypalOffered && (
                    <TabsTrigger value="paypal" className="min-w-0 whitespace-normal px-1 py-1.5 text-[11px] leading-tight sm:px-3 sm:text-sm sm:whitespace-nowrap">
                      <Globe className="mb-0.5 h-3.5 w-3.5 shrink-0 sm:mb-0 sm:mr-2 sm:h-4 sm:w-4" />
                      <span className="truncate">{i18n.__('vaiu.topup.tabs.paypal')}</span>
                    </TabsTrigger>
                  )}
                </TabsList>
              )}

              {/* Show Stripe minimum warning only for Stripe/card payment methods, not PayPal */}
              {topUpAmount > 0 && 
               !meetsStripeMinimum(topUpAmount, currency) && 
               (selectedTab === 'card' || selectedTab === 'digital-wallet' || selectedTab === 'atm') && (
                <Alert variant="destructive" className="mt-2">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{getStripeMinimumMessage(currency)}</AlertDescription>
                </Alert>
              )}

              {/* ATM Tab - Only show if card-to-token enabled, shortfall exists, and Stripe supports currency */}
              {isCardToTokenEnabled() && amounts.shortfall > 0 && stripeOk && (
                <TabsContent value="atm" className="space-y-4">
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      {i18n.__('vaiu.topup.atm.description')}
                    </AlertDescription>
                  </Alert>
                  <PaymentMethodATM
                    amount={topUpAmount}
                    currency={currency}
                    onSuccess={handleATMSuccess}
                    onError={(err) => setError(err)}
                    onCancel={onCancel}
                    disabled={loading || topUpAmount < amounts.minimumTopUp}
                  />
                </TabsContent>
              )}

              {/* Card Tab - Only show if Stripe supports currency */}
              {stripeOk && activeFundingSource === 'card' && (
                <TabsContent value="card" className="space-y-4">
                {showCardEntryForm ? (
                  <PaymentMethodCardForm
                    amount={topUpAmount}
                    currency={currency}
                    onSubmit={handleCardAdded}
                    onCancel={() => {
                      if (hasSavedCardMethods) {
                        setShowAddCardForm(false);
                        setSelectedMethodId(pickDefaultSavedCardMethodId());
                      } else {
                        onCancel();
                      }
                    }}
                    isGuest={false}
                    disabled={loading || topUpAmount < amounts.minimumTopUp || !meetsStripeMinimum(topUpAmount, currency)}
                    showSeparator={false}
                    submitLabel={chargeCardLabel}
                    loadingLabel={chargingCardLabel}
                    skipPlatformSave
                  />
                ) : (
                  <>
                    {isTokenCreationFlow ? (
                      <p className="text-sm text-muted-foreground">
                        The selected card is shown in the funding-source rail above.
                      </p>
                    ) : (
                    <div className="space-y-2">
                        <Label htmlFor="payment-method">{i18n.__('vaiu.topup.select_method')}</Label>
                  <Select 
                    value={
                      showAddCardForm
                        ? 'add-new-card'
                        : isSavedCardOrBankMethodId(selectedMethodId)
                          ? selectedMethodId
                          : pickDefaultSavedCardMethodId() || undefined
                    }
                    onValueChange={(value) => {
                            if (value === 'add-new-card') {
                              setSelectedMethodId('add-new-card');
                              setShowAddCardForm(true);
                            } else {
                              setSelectedMethodId(value);
                              setShowAddCardForm(false);
                            }
                    }}
                  >
                    <SelectTrigger><SelectValue placeholder={i18n.__('vaiu.topup.select_method_placeholder')} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="add-new-card">
                              <div className="flex items-center space-x-2">
                                <CreditCard className="w-4 h-4" />
                                <span>{i18n.__('vaiu.topup.add_new_card')}</span>
                              </div>
                      </SelectItem>
                            {savedCardMethods.map((method) => (
                        <SelectItem key={method.id} value={method.id!}>
                                  <div className="flex items-center space-x-2">
                                    {getMethodIcon(method.type)}
                                    <span>{getMethodDisplayName(method)}</span>
                                  </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                    )}
                    
                    {isSavedCardOrBankMethodId(selectedMethodId) && (
                <div className="flex min-w-0 flex-col gap-2 pt-2 sm:flex-row sm:gap-3">
                        <Button onClick={onCancel} disabled={loading} className="min-w-0 flex-1">{i18n.__('common.cancel')}</Button>
                        <Button 
                          onClick={handleTopUp} 
                          disabled={loading || !selectedMethodId || !meetsStripeMinimum(topUpAmount, currency)} 
                          className="min-w-0 flex-1"
                        >
                    {loading ? chargingCardLabel : chargeCardLabel}
                  </Button>
                </div>
                    )}
                  </>
                )}
              </TabsContent>
              )}

              {bankOk && activeFundingSource === 'bank' && (
                <TabsContent value="card" className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    The selected bank account is shown in the funding-source rail above.
                  </p>
                  {isSavedCardOrBankMethodId(selectedMethodId) && (
                    <div className="flex min-w-0 flex-col gap-2 pt-2 sm:flex-row sm:gap-3">
                      <Button onClick={onCancel} disabled={loading} className="min-w-0 flex-1">{i18n.__('common.cancel')}</Button>
                      <Button
                        onClick={handleTopUp}
                        disabled={loading || !selectedMethodId || !meetsStripeMinimum(topUpAmount, currency)}
                        className="min-w-0 flex-1"
                      >
                        {loading ? 'Charging bank account...' : 'Charge bank account and create token'}
                      </Button>
                    </div>
                  )}
                </TabsContent>
              )}

              {/* PayPal Tab - Only show if PayPal supports currency */}
              {paypalOffered && activeFundingSource === 'paypal' && (
                <TabsContent value="paypal" className="space-y-4">
                  <div className="space-y-3 border-t border-dashed border-muted-foreground/30 pt-4">
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4 text-muted-foreground" />
                      <h4 className="font-semibold text-xs uppercase tracking-wide text-muted-foreground">
                        {i18n.__('vaiu.topup.tabs.paypal')}
                      </h4>
                    </div>
                    <PaymentMethodPayPal
                      amount={topUpAmount}
                      currency={currency}
                      onSelect={() => {
                        setSelectedMethodId('paypal-funding');
                      }}
                      onPay={() => {
                        setSelectedMethodId('paypal-funding');
                        handleTopUp();
                      }}
                      disabled={loading || (!isTokenCreationFlow && topUpAmount < amounts.shortfall) || topUpAmount <= 0}
                      isSelected={selectedMethodId === 'paypal-funding'}
                      approvalHref={paypalApprovalHref}
                    />
                  </div>
                  {selectedMethodId === 'paypal-funding' && (
                    <div className="pt-2">
                      <Button onClick={onCancel} disabled={loading} variant="outline" className="w-full">
                        {i18n.__('common.cancel')}
                      </Button>
                    </div>
                  )}
                </TabsContent>
              )}

              {/* Digital Wallet Tab - Only show if Stripe supports currency */}
              {stripeOk && ['wallet', 'bitcoin', 'ethereum'].includes(activeFundingSource) && (
                <TabsContent value="digital-wallet" className="space-y-6">
                {/* Digital Wallets Section (Apple Pay / Google Pay) */}
                {activeFundingSource === 'wallet' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Smartphone className="w-5 h-5 text-muted-foreground" />
                    <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">{i18n.__('vaiu.topup.digital_wallets')}</h3>
                  </div>
                  <PaymentMethodDigitalWallet
                    amount={topUpAmount}
                    currency={currency}
                    onPaymentSuccess={handleDigitalWalletPayment}
                    onPaymentError={(err) => setError(err.message)}
                    disabled={loading || topUpAmount < amounts.minimumTopUp || !meetsStripeMinimum(topUpAmount, currency)}
                    showSeparator={false}
                  />
                </div>
                )}

                {/* Crypto Section */}
                {(activeFundingSource === 'bitcoin' || activeFundingSource === 'ethereum') && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Wallet className="w-5 h-5 text-muted-foreground" />
                    <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">{i18n.__('vaiu.topup.crypto')}</h3>
                  </div>
                  
                  {/* Bitcoin */}
                  {activeFundingSource === 'bitcoin' && (bitcoinOk) && (
                    <PaymentMethodBitcoin
                      amount={topUpAmount}
                      currency={currency}
                      onSuccess={(newBalance, fundingDetails) => {
                        // Handle Bitcoin payment success
                        emitTopUpBalanceUpdate(
                          topUpAmount,
                          currency,
                          currentBalance,
                          fundingDetails?.transactionId || `btc_${Date.now()}`
                        );
                        onSuccess(newBalance, fundingDetails);
                      }}
                      onError={(err) => setError(err)}
                      onCancel={onCancel}
                      disabled={loading || topUpAmount < amounts.minimumTopUp}
                      paymentMethods={paymentMethods}
                      showSeparator={false}
                    />
                  )}

                  {/* Ethereum */}
                  {activeFundingSource === 'ethereum' && (ethereumOk) && (
                    <PaymentMethodEthereum
                      amount={topUpAmount}
                      currency={currency}
                      onSuccess={(newBalance, fundingDetails) => {
                        // Handle Ethereum payment success
                        emitTopUpBalanceUpdate(
                          topUpAmount,
                          currency,
                          currentBalance,
                          fundingDetails?.transactionId || `eth_${Date.now()}`
                        );
                        onSuccess(newBalance, fundingDetails);
                      }}
                      onError={(err) => setError(err)}
                      onCancel={onCancel}
                      disabled={loading || topUpAmount < amounts.minimumTopUp}
                      showSeparator={bitcoinOk}
                    />
                  )}

                </div>
                )}
              </TabsContent>
              )}
            </Tabs>
            )}

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );

  // Return the main content directly since we're rendering inline
  return mainContent;
};

export default TopUpBalance;
